import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Search, Plus, X, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const parseContactTags = (tagsValue) => {
  if (!tagsValue) return [];
  if (Array.isArray(tagsValue)) return tagsValue.map(tag => String(tag).trim()).filter(Boolean);

  try {
    const parsed = JSON.parse(tagsValue);
    if (Array.isArray(parsed)) return parsed.map(tag => String(tag).trim()).filter(Boolean);
  } catch (error) {
    // Use the legacy comma-separated format.
  }

  return String(tagsValue).split(',').map(tag => tag.trim()).filter(Boolean);
};

const ContactManagementPanel = ({ isOpen, onClose, onRecipientsChanged, selectedCampaign }) => {
  const { user } = useAuth();
  const dropdownRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    clientType: 'All Client Types',
    source: 'All Sources',
    name: '',
    company: '',
    tag: 'All Tags'
  });
  const [debouncedSearch, setDebouncedSearch] = useState({ name: '', company: '' });
  const [tagSearch, setTagSearch] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Map());
  const [selectAll, setSelectAll] = useState(false);
  const [view, setView] = useState('current');
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [loadingCampaignRecipients, setLoadingCampaignRecipients] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState(new Set());
  const [currentSearch, setCurrentSearch] = useState('');
  const [currentStatus, setCurrentStatus] = useState('all');
  const [currentRecipientPage, setCurrentRecipientPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 15
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const getRecipientSequenceStatus = useCallback((recipient) => {
    if (recipient.recipient_status === 'paused') return 'paused';

    const templateCount = selectedCampaign?.templates?.length || 0;
    if (recipient.recipient_status === 'completed' ||
        (templateCount > 0 && Number(recipient.round_number) >= templateCount)) {
      return 'completed';
    }

    return 'sending';
  }, [selectedCampaign?.templates?.length]);

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add pagination parameters
      params.append('page', currentPage.toString());
      params.append('limit', '15');

      // Add sorting parameters
      if (sortConfig.key) {
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
      }

      // Apply filters
      if (filters.clientType !== 'All Client Types') {
        params.append('clientType', filters.clientType);
      }
      if (filters.source !== 'All Sources') {
        params.append('source', filters.source);
      }
      if (debouncedSearch.name) {
        params.append('name', debouncedSearch.name);
      }
      if (debouncedSearch.company) {
        params.append('company', debouncedSearch.company);
      }
      if (filters.tag !== 'All Tags') {
        // Always use individual tag filtering since backend doesn't support multiple tags
        params.append('tag', filters.tag);
      }

      // Filter by current user only
      //params.append('userId', user?.id.toString());

      // Exclude contacts with pause state
      params.append('excludePaused', 'true');

      // Exclude contacts already in the current campaign if we have a selected campaign
      if (selectedCampaign?.id) {
        console.log('ContactManagementPanel: Excluding contacts from campaign:', selectedCampaign.id);
        params.append('excludeCampaign', selectedCampaign.id.toString());
      }

      const response = await axios.get(`/api/contact-management/contacts?${params.toString()}`);
      if (response.data.success) {
        setContacts(response.data.contacts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
      alert('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [
    filters.clientType,
    filters.source,
    filters.tag,
    debouncedSearch,
    currentPage,
    sortConfig,
    selectedCampaign?.id
  ]);

  const loadTagsAndFolders = useCallback(async () => {
    try {
      // Load both folders and tags
      const [foldersResponse, tagsResponse] = await Promise.all([
        axios.get('/api/contact-management/folders'),
        axios.get('/api/contact-management/tags')
      ]);

      const folders = foldersResponse.data.success && Array.isArray(foldersResponse.data.folders) ? foldersResponse.data.folders : [];
      const allTags = tagsResponse.data.success && Array.isArray(tagsResponse.data.tags) ? tagsResponse.data.tags : [];

      setAvailableFolders(folders);
      setAvailableTags(allTags);
    } catch (error) {
      console.error('Failed to load tags and folders:', error);
      setAvailableTags([]);
      setAvailableFolders([]);
    }
  }, []);

  const loadCampaignRecipients = useCallback(async () => {
    if (!selectedCampaign?.id) return [];

    try {
      setLoadingCampaignRecipients(true);
      const response = await axios.get(`/api/email-campaigns/${selectedCampaign.id}/recipients`);
      const recipients = response.data.success ? response.data.recipients || [] : [];
      setCampaignRecipients(recipients);
      setCurrentRecipientPage(1);
      return recipients;
    } catch (error) {
      console.error('Failed to load campaign recipients:', error);
      alert('Failed to load campaign contacts');
      return [];
    } finally {
      setLoadingCampaignRecipients(false);
    }
  }, [selectedCampaign?.id]);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadContacts();
    }
  }, [isOpen, loadContacts, user?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch({
        name: filters.name.trim(),
        company: filters.company.trim()
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.name, filters.company]);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadTagsAndFolders();
      loadCampaignRecipients();
      setView('current');
      setSelectedContacts(new Map());
      setSelectedRecipientIds(new Set());
    }
  }, [isOpen, loadTagsAndFolders, loadCampaignRecipients, user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setTagSearch('');
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  // Helper function to get unorganized tags (tags not in any folder)
  const getUnorganizedTags = useCallback(() => {
    if (!Array.isArray(availableFolders) || !Array.isArray(availableTags)) {
      return [];
    }

    // Get all tags that are included in folders
    const organizedTags = new Set();
    availableFolders.forEach(folder => {
      if (folder.include_tags) {
        const tags = folder.include_tags.split(',').map(tag => tag.trim());
        tags.forEach(tag => organizedTags.add(tag));
      }
    });

    // Return tags that are not in any folder
    return availableTags.filter(tag => !organizedTags.has(tag));
  }, [availableTags, availableFolders]);

  // Handle folder expansion toggle
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Handle tag selection from dropdown
  const handleTagSelection = (tagValue) => {
    handleFilterChange('tag', tagValue);
    setTagSearch('');
    setDropdownOpen(false);
  };

  const filteredTagOptions = useMemo(() => {
    const search = tagSearch.trim().toLowerCase();
    if (!search) return availableTags;
    return availableTags.filter(tag => String(tag).toLowerCase().includes(search));
  }, [availableTags, tagSearch]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      clientType: 'All Client Types',
      source: 'All Sources',
      name: '',
      company: '',
      tag: 'All Tags'
    });
    setDebouncedSearch({ name: '', company: '' });
    setTagSearch('');
    setCurrentPage(1);
  };

  const handleSelectContact = (contactId, contactStatus, contact) => {
    const contactKey = `${contactStatus}-${contactId}`;
    setSelectedContacts(prev => {
      const newSelected = new Map(prev);
      if (newSelected.has(contactKey)) {
        newSelected.delete(contactKey);
      } else {
        newSelected.set(contactKey, contact);
      }
      return newSelected;
    });
  };

  const handleSelectAll = async () => {
    setSelectingAll(true);
    try {
      // Fetch all filtered contacts (not just current page)
      const params = new URLSearchParams();

      // Apply same filters as loadContacts but without pagination
      if (filters.clientType !== 'All Client Types') {
        params.append('clientType', filters.clientType);
      }
      if (filters.source !== 'All Sources') {
        params.append('source', filters.source);
      }
      if (debouncedSearch.name) {
        params.append('name', debouncedSearch.name);
      }
      if (debouncedSearch.company) {
        params.append('company', debouncedSearch.company);
      }
      if (filters.tag !== 'All Tags') {
        // Always use individual tag filtering since backend doesn't support multiple tags
        params.append('tag', filters.tag);
      }

      // Load all contacts that match the current filters.
      params.append('limit', String(Math.max(pagination.total, 15)));
      params.append('page', '1');

      // Exclude current campaign contacts
      if (selectedCampaign?.id) {
        params.append('excludeCampaign', selectedCampaign.id.toString());
      }
      params.append('excludePaused', 'true');

      const response = await axios.get(`/api/contact-management/contacts?${params.toString()}`);

      if (response.data.success) {
        const allFilteredContacts = response.data.contacts || [];

        // Create map of all filtered contacts
        const allContactsMap = new Map(
          allFilteredContacts.map(contact => [
            `${contact.contact_status}-${contact.id}`,
            contact
          ])
        );

        // Replace selectedContacts with all filtered contacts
        setSelectedContacts(allContactsMap);
        setSelectAll(true);
      }
    } catch (error) {
      console.error('Failed to load all filtered contacts for selection:', error);
      // Fallback to current page only
      const allContactsMap = new Map(
        contacts.map(contact => [
          `${contact.contact_status}-${contact.id}`,
          contact
        ])
      );
      setSelectedContacts(prev => {
        const newSelected = new Map(prev);
        allContactsMap.forEach((value, key) => newSelected.set(key, value));
        return newSelected;
      });
      setSelectAll(true);
    } finally {
      setSelectingAll(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedContacts(new Map());
    setSelectAll(false);
  };

  const handleAddToCampaign = async () => {
    if (selectedContacts.size === 0) {
      alert('Please select contacts to add to the campaign.');
      return;
    }

    const selectedContactsArray = Array.from(selectedContacts.values());
    try {
      setSaving(true);
      const response = await axios.post(`/api/email-campaigns/${selectedCampaign.id}/add-contacts`, {
        contacts: selectedContactsArray.map(contact => ({
          id: contact.id,
          email: contact.email,
          first_name: contact.first_name,
          last_name: contact.last_name,
          company: contact.company,
          job_title: contact.job_title,
          phone: contact.phone,
          location: contact.location,
          country: contact.country
        }))
      });

      const addedCount = Number(response.data.addedCount) || 0;
      const skippedCount = Number(response.data.skippedCount) || 0;
      const updatedRecipients = await loadCampaignRecipients();
      await loadContacts();
      onRecipientsChanged?.(updatedRecipients);
      handleClearSelection();
      setView('current');

      const skippedMessage = skippedCount > 0 ? ` ${skippedCount} contacts were skipped.` : '';
      alert(`${addedCount} contacts added to the campaign.${skippedMessage}`);
    } catch (error) {
      console.error('Failed to add contacts to campaign:', error);
      alert('Failed to add contacts: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const filteredCampaignRecipients = useMemo(() => {
    const search = currentSearch.trim().toLowerCase();

    return campaignRecipients.filter(recipient => {
      const status = getRecipientSequenceStatus(recipient);
      if (currentStatus === 'sending' && ['paused', 'completed'].includes(status)) return false;
      if (currentStatus !== 'all' && currentStatus !== 'sending' && status !== currentStatus) return false;
      if (!search) return true;

      return [
        recipient.first_name,
        recipient.last_name,
        recipient.email,
        recipient.company
      ].some(value => String(value || '').toLowerCase().includes(search));
    });
  }, [campaignRecipients, currentSearch, currentStatus, getRecipientSequenceStatus]);

  const currentRecipientTotalPages = Math.max(1, Math.ceil(filteredCampaignRecipients.length / 15));
  const visibleCampaignRecipients = filteredCampaignRecipients.slice(
    (currentRecipientPage - 1) * 15,
    currentRecipientPage * 15
  );

  const toggleCampaignRecipient = (recipientId) => {
    setSelectedRecipientIds(previous => {
      const next = new Set(previous);
      if (next.has(recipientId)) next.delete(recipientId);
      else next.add(recipientId);
      return next;
    });
  };

  const clearRecipientSelection = () => setSelectedRecipientIds(new Set());

  const selectableVisibleRecipients = visibleCampaignRecipients.filter(
    recipient => getRecipientSequenceStatus(recipient) !== 'completed'
  );
  const allVisibleRecipientsSelected = selectableVisibleRecipients.length > 0 &&
    selectableVisibleRecipients.every(recipient => selectedRecipientIds.has(recipient.id));

  const toggleVisibleCampaignRecipients = () => {
    setSelectedRecipientIds(previous => {
      const next = new Set(previous);
      selectableVisibleRecipients.forEach(recipient => {
        if (allVisibleRecipientsSelected) next.delete(recipient.id);
        else next.add(recipient.id);
      });
      return next;
    });
  };

  const updateSelectedRecipientStatus = async (status) => {
    const eligibleIds = campaignRecipients
      .filter(recipient => selectedRecipientIds.has(recipient.id))
      .filter(recipient => status === 'paused'
        ? getRecipientSequenceStatus(recipient) === 'sending'
        : getRecipientSequenceStatus(recipient) === 'paused')
      .map(recipient => recipient.id);

    if (eligibleIds.length === 0) return;

    const action = status === 'paused' ? 'stop future emails for' : 'resume future emails for';
    if (!window.confirm(`Do you want to ${action} ${eligibleIds.length} contacts?`)) return;

    try {
      setSaving(true);
      const response = await axios.put(`/api/email-campaigns/${selectedCampaign.id}/recipients/status`, {
        recipient_ids: eligibleIds,
        recipient_status: status
      });
      const updatedRecipients = await loadCampaignRecipients();
      onRecipientsChanged?.(updatedRecipients);
      clearRecipientSelection();
      alert(`${response.data.updatedCount || 0} campaign contacts updated.`);
    } catch (error) {
      console.error('Failed to update campaign contacts:', error);
      alert('Failed to update campaign contacts: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const stoppableCount = campaignRecipients.filter(recipient =>
    selectedRecipientIds.has(recipient.id) &&
    getRecipientSequenceStatus(recipient) === 'sending'
  ).length;
  const resumableCount = campaignRecipients.filter(recipient =>
    selectedRecipientIds.has(recipient.id) && getRecipientSequenceStatus(recipient) === 'paused'
  ).length;

  useEffect(() => {
    setCurrentRecipientPage(1);
  }, [currentSearch, currentStatus]);

  const getContactStatusColor = (contactStatus) => {
    switch (contactStatus) {
      case 'cold': return 'bg-blue-100 text-blue-700';
      case 'warm': return 'bg-orange-100 text-orange-700';
      case 'active': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getContactStatusLabel = (contactStatus) => {
    switch (contactStatus) {
      case 'cold': return 'Cold';
      case 'warm': return 'Warm';
      case 'active': return 'Active';
      default: return contactStatus;
    }
  };

  // Update selectAll state when selectedContacts changes
  useEffect(() => {
    if (contacts.length > 0) {
      const allSelected = contacts.every(contact =>
        selectedContacts.has(`${contact.contact_status}-${contact.id}`)
      );
      setSelectAll(allSelected);
    }
  }, [selectedContacts, contacts]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b bg-gray-50 flex-shrink-0 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Manage Campaign Contacts
                {selectedCampaign && `: ${selectedCampaign.name}`}
              </h2>
              <p className="text-gray-600 mt-1">
                Review current recipients or add contacts to the campaign.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close contact manager"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="px-4 pt-4 border-b bg-white flex-shrink-0">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setView('current');
                handleClearSelection();
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 ${view === 'current'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              In Campaign ({campaignRecipients.length})
            </button>
            <button
              type="button"
              onClick={() => {
                setView('available');
                clearRecipientSelection();
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 ${view === 'available'
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Available to Add ({pagination.total || 0})
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          {view === 'current' ? (
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="relative flex-1 min-w-64 max-w-lg">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by name, email, or company"
                  value={currentSearch}
                  onChange={(event) => setCurrentSearch(event.target.value)}
                  className="w-full py-2 pl-10 pr-3 border border-gray-300 rounded-lg"
                />
              </div>
              <select
                value={currentStatus}
                onChange={(event) => setCurrentStatus(event.target.value)}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All campaign statuses</option>
                <option value="sending">Active in sequence</option>
                <option value="paused">Future emails stopped</option>
                <option value="completed">Sequence completed</option>
              </select>
              <span className="text-sm text-gray-500">{filteredCampaignRecipients.length} contacts</span>
            </div>
          ) : (
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-1 flex-wrap gap-3">
              <div className="relative min-w-56 flex-1">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search name or email..."
                  value={filters.name}
                  onChange={(event) => handleFilterChange('name', event.target.value)}
                  className="w-full py-2 pl-10 pr-3 border border-gray-300 rounded-lg"
                />
              </div>
              <input
                type="text"
                placeholder="Filter company..."
                value={filters.company}
                onChange={(event) => handleFilterChange('company', event.target.value)}
                className="p-2 border border-gray-300 rounded-lg min-w-48"
              />
              <select
                value={filters.clientType}
                onChange={(e) => handleFilterChange('clientType', e.target.value)}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option>All Client Types</option>
                <option>Cold Clients</option>
                <option>Warm Clients</option>
                <option>Active Clients</option>
              </select>
              <select
                value={filters.source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option>All Sources</option>
                <option>scraping</option>
                <option>manual</option>
                <option>form</option>
                <option>email_forward</option>
                <option>zoho</option>
              </select>
              {/* Custom dropdown for hierarchical tags */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="p-2 border border-gray-300 rounded-lg bg-white text-left min-w-48 flex justify-between items-center hover:border-gray-400"
                >
                  <span>{filters.tag === 'All Tags' ? 'All Tags' : filters.tag}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                    <div className="sticky top-0 z-10 p-2 bg-white border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2 text-gray-400" size={16} />
                        <input
                          type="text"
                          value={tagSearch}
                          onChange={(event) => setTagSearch(event.target.value)}
                          placeholder="Search tags..."
                          className="w-full py-1.5 pl-8 pr-2 text-sm border border-gray-300 rounded-md"
                          autoFocus
                        />
                      </div>
                    </div>
                    {/* All Tags option */}
                    <button
                      type="button"
                      onClick={() => handleTagSelection('All Tags')}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b"
                    >
                      All Tags
                    </button>

                    {tagSearch.trim() ? (
                      filteredTagOptions.length > 0 ? filteredTagOptions.map(tag => (
                        <button
                          type="button"
                          key={`search-tag-${tag}`}
                          onClick={() => handleTagSelection(tag)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                        >
                          {tag}
                        </button>
                      )) : (
                        <p className="px-3 py-4 text-sm text-center text-gray-500">No tags match your search.</p>
                      )
                    ) : (<>
                    {/* Organized folders */}
                    {Array.isArray(availableFolders) && availableFolders.map(folder => {
                      const folderTags = folder.include_tags ? folder.include_tags.split(',').map(tag => tag.trim()) : [];
                      const isExpanded = expandedFolders.has(folder.id);

                      return (
                        <div key={`folder-${folder.id}`}>
                          {/* Folder header - clickable to expand/collapse */}
                          <button
                            type="button"
                            onClick={() => toggleFolder(folder.id)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between font-medium"
                          >
                            <span>📁 {folder.name}</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Expanded folder tags */}
                          {isExpanded && folderTags.map(tag => (
                            <button
                              type="button"
                              key={`folder-${folder.id}-tag-${tag}`}
                              onClick={() => handleTagSelection(tag)}
                              className="w-full text-left px-6 py-1 hover:bg-gray-100 text-sm text-gray-700"
                            >
                              🏷️ {tag}
                            </button>
                          ))}
                        </div>
                      );
                    })}

                    {/* Unorganized tags */}
                    {getUnorganizedTags().length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-sm font-medium text-gray-500 border-t">
                          Unorganized Tags
                        </div>
                        {getUnorganizedTags().map(tag => (
                          <button
                            type="button"
                            key={`unorganized-${tag}`}
                            onClick={() => handleTagSelection(tag)}
                            className="w-full text-left px-6 py-1 hover:bg-gray-100 text-sm"
                          >
                            🏷️ {tag}
                          </button>
                        ))}
                      </div>
                    )}
                    </>)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-3 py-1 text-sm bg-white text-gray-700 border rounded hover:bg-gray-50"
              >
                Clear filters
              </button>
              <button
                onClick={handleSelectAll}
                disabled={pagination.total === 0 || selectingAll}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
              >
                {selectingAll ? 'Selecting...' : `Select All (${pagination.total || 0})`}
              </button>
              <button
                onClick={handleClearSelection}
                disabled={selectedContacts.size === 0}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Action Bar */}
        <div className="p-4 border-b bg-blue-50 flex-shrink-0">
          {view === 'current' ? (
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div>
                <span className="text-blue-700 font-medium">{selectedRecipientIds.size} contacts selected</span>
                <p className="text-xs text-gray-500 mt-1">
                  Stopping future emails keeps all campaign history and metrics.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={clearRecipientSelection}
                  disabled={selectedRecipientIds.size === 0 || saving}
                  className="px-3 py-2 text-sm bg-white text-gray-700 border rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => updateSelectedRecipientStatus('paused')}
                  disabled={stoppableCount === 0 || saving}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Stop Future Emails ({stoppableCount})
                </button>
                <button
                  type="button"
                  onClick={() => updateSelectedRecipientStatus('sending')}
                  disabled={resumableCount === 0 || saving}
                  className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Resume Future Emails ({resumableCount})
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-between items-center gap-3">
              <span className="text-blue-700 font-medium">{selectedContacts.size} contacts selected</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleClearSelection}
                  disabled={selectedContacts.size === 0 || saving}
                  className="px-3 py-2 text-sm bg-white text-gray-700 border rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleAddToCampaign}
                  disabled={selectedContacts.size === 0 || saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus size={16} />
                  {saving ? 'Adding...' : `Add to Campaign (${selectedContacts.size})`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Contacts Table - Scrollable Area */}
        <div className={`flex-1 overflow-hidden flex flex-col min-h-0 ${pagination.totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
          {(view === 'current' ? loadingCampaignRecipients : loading) ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
                <p>Loading contacts...</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              {view === 'current' ? (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={allVisibleRecipientsSelected}
                          onChange={toggleVisibleCampaignRecipients}
                          disabled={selectableVisibleRecipients.length === 0}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label="Select visible campaign contacts"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sequence Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Step</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Sent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Scheduled</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {visibleCampaignRecipients.map(recipient => {
                      const status = getRecipientSequenceStatus(recipient);
                      const completed = status === 'completed';
                      return (
                        <tr key={recipient.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedRecipientIds.has(recipient.id)}
                              onChange={() => toggleCampaignRecipient(recipient.id)}
                              disabled={completed}
                              className="rounded text-blue-600 focus:ring-blue-500 border-gray-300 disabled:opacity-40"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {recipient.first_name} {recipient.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{recipient.email}</div>
                            {recipient.company && <div className="text-xs text-gray-400">{recipient.company}</div>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              status === 'paused'
                                ? 'bg-red-100 text-red-700'
                                : completed
                                  ? 'bg-gray-100 text-gray-700'
                                  : 'bg-green-100 text-green-700'
                            }`}>
                              {status === 'paused' ? 'Future emails stopped' : completed ? 'Completed' : 'Active'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {completed
                              ? 'Sequence completed'
                              : Number(recipient.round_number) === 0
                                ? 'Initial email'
                                : `Follow-up ${Number(recipient.round_number) + 1}`}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {recipient.last_sent_at ? new Date(recipient.last_sent_at).toLocaleDateString() : 'Not sent'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                            {completed
                              ? 'Complete'
                              : recipient.next_scheduled_at
                                ? new Date(recipient.next_scheduled_at).toLocaleDateString()
                                : 'Not scheduled'}
                          </td>
                        </tr>
                      );
                    })}
                    {visibleCampaignRecipients.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                          No campaign contacts match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
              <table className="w-full min-w-[1120px]">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={() => selectAll ? handleClearSelection() : handleSelectAll()}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label="Select all filtered contacts"
                        />
                    </th>
                    <th
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('contact')}
                    >
                      <div className="flex items-center gap-1">
                        Contact
                        {sortConfig.key === 'contact' && (
                          sortConfig.direction === 'asc' ?
                            <ChevronUp size={14} /> :
                            <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('company')}
                    >
                      <div className="flex items-center gap-1">
                        Company
                        {sortConfig.key === 'company' && (
                          sortConfig.direction === 'asc' ?
                            <ChevronUp size={14} /> :
                            <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map(contact => {
                    const contactTags = parseContactTags(contact.tags);
                    return (
                    <tr key={`${contact.contact_status}-${contact.id}`} className="hover:bg-gray-50 h-14">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedContacts.has(`${contact.contact_status}-${contact.id}`)}
                          onChange={() => handleSelectContact(contact.id, contact.contact_status, contact)}
                          className="rounded text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Unnamed Contact'}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 max-w-56 break-all">
                        {contact.email || '—'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 max-w-48">
                        <span className="line-clamp-2">{contact.company || '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 max-w-48">
                        <span className="line-clamp-2">{contact.job_title || '—'}</span>
                      </td>
                      <td className="px-3 py-3 max-w-56">
                        <div className="flex flex-wrap gap-1">
                          {contactTags.slice(0, 2).map(tag => (
                            <span
                              key={tag}
                              className={`px-2 py-0.5 rounded-full text-xs ${tag === 'block'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {tag === 'block' ? 'DNC' : tag}
                            </span>
                          ))}
                          {contactTags.length > 2 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                              +{contactTags.length - 2}
                            </span>
                          )}
                          {contactTags.length === 0 && <span className="text-sm text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getContactStatusColor(contact.contact_status)}`}>
                          {getContactStatusLabel(contact.contact_status)}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600">
                        {contact.source || '—'}
                      </td>
                    </tr>
                    );
                  })}
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                        No available contacts match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0 rounded-b-lg">
          <div className="flex justify-between items-center gap-3">
            <div className="text-sm text-gray-600">
              {view === 'current'
                ? `Page ${currentRecipientPage} of ${currentRecipientTotalPages}`
                : `Page ${pagination.currentPage} of ${Math.max(1, pagination.totalPages)}`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => view === 'current'
                  ? setCurrentRecipientPage(previous => Math.max(1, previous - 1))
                  : setCurrentPage(previous => Math.max(1, previous - 1))}
                disabled={saving || (view === 'current' ? currentRecipientPage === 1 : currentPage === 1)}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => view === 'current'
                  ? setCurrentRecipientPage(previous => Math.min(currentRecipientTotalPages, previous + 1))
                  : setCurrentPage(previous => Math.min(pagination.totalPages, previous + 1))}
                disabled={saving || (view === 'current'
                  ? currentRecipientPage >= currentRecipientTotalPages
                  : currentPage >= pagination.totalPages)}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="ml-2 px-4 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactManagementPanel;
